/**
 * Roles (MB, 18 Oct 2026; docs/04-security-and-tenancy.md §2) and the pages each may open. This table is for
 * display only: what a user can read or change is decided by RLS and the Edge Functions. It moves to
 * `packages/domain` as `can(user, action, resource)` when the first write action beyond People and Users & roles arrives.
 *
 * A staff member may hold several tenant roles at once; their access is the union of those roles, so every check
 * here is any-of (`hasAnyRole`, `navFor`). A client user holds one role per client organisation.
 */
import { canInsights } from '@teslyn/domain';

export const TENANT_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
  // R156, decision 917: the phone-only sign-in. No Teslyn Cloud page (NAV.pilot is empty); a pilot-only sign-in sees
  // the pilot page (App.tsx `PilotOnly`), and the database gives it nothing here (20261103100100)
  'pilot',
] as const;
export const CLIENT_ROLES = ['client_admin', 'client_viewer'] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];
export type ClientRole = (typeof CLIENT_ROLES)[number];

export const ROLE_LABEL: Record<TenantRole | ClientRole | 'platform_admin', string> = {
  tenant_admin: 'Tenant admin',
  manager: 'Manager',
  field_crew: 'Field crew',
  office_staff: 'Office staff',
  data_processor: 'Data processor',
  pilot: 'Pilot (phone only)',
  client_admin: 'Client admin',
  client_viewer: 'Client viewer',
  platform_admin: 'Platform admin',
};

/** Tenant roles de-duplicated and in TENANT_ROLES order (the order chips and checkboxes use). */
export function sortTenantRoles(roles: Iterable<TenantRole>): TenantRole[] {
  const set = new Set(roles);
  return TENANT_ROLES.filter((r) => set.has(r));
}

/** True when any of `roles` is one of `wanted`. */
export function hasAnyRole(roles: readonly string[], ...wanted: readonly string[]): boolean {
  return roles.some((r) => wanted.includes(r));
}

export function isTenantRole(v: string): v is TenantRole {
  return (TENANT_ROLES as readonly string[]).includes(v);
}
export function isClientRole(v: string): v is ClientRole {
  return (CLIENT_ROLES as readonly string[]).includes(v);
}

/**
 * The M0 enum still has `office_processor` (renamed and split by MB on 18 Oct 2026). Until the enum migration
 * lands, a row with that value is shown with data_processor access, the closer of the two new roles.
 */
export function normaliseTenantRole(v: string): TenantRole | null {
  if (isTenantRole(v)) return v;
  if (v === 'office_processor') return 'data_processor';
  return null;
}

/**
 * The pages of Teslyn Cloud. Since the menu consolidation (R126; decisions 841 to 844, 855): Progress, Deleted
 * projects and Flights are part of Projects; Staff and Users & roles are one People page (client admins keep their own
 * Users page, Braynewave's console its Users & roles); Company and documents and Settings are one Company page; your
 * own sign-in security opens from the top bar ("My sign-in"). The old ids still open the new places (`MOVED_PAGES`).
 */
export type PageId =
  | 'home'
  | 'projects'
  | 'reports'
  | 'insights'
  | 'deliverables'
  | 'approvals'
  | 'notices'
  | 'ops'
  | 'tracking'
  | 'alerts'
  | 'stations'
  | 'spaceweather'
  | 'hazard-packs'
  | 'safety-signoff'
  | 'safety-register'
  | 'clients'
  | 'quotes'
  | 'contracts'
  | 'preview'
  | 'people'
  | 'licences'
  | 'users'
  | 'aircraft'
  | 'company'
  | 'integrations'
  | 'exports'
  | 'diagnostics'
  | 'mobile-app'
  | 'security'
  | 'tenants'
  | 'devices'
  | 'invoicing'
  | 'audit';

export interface PageDef {
  readonly id: PageId;
  readonly label: string;
  /** The Home card's words: what the page does now (C10, decision 844); "Coming soon" pages say so. */
  readonly summary: string;
  /** The client portal's words, where they differ from the staff workspace's. */
  readonly clientSummary?: string;
  /** The staff workspace's words, where they differ from Braynewave's console's (the Audit log, R166). */
  readonly workspaceSummary?: string;
  /** Not built yet: the page shows "Coming soon" with the summary. */
  readonly soon?: boolean;
}

/** In menu order (the headings of MENU_GROUPS); a person with several roles sees their pages in this order. */
export const PAGES: Record<PageId, PageDef> = {
  home: { id: 'home', label: 'Home', summary: '' },
  projects: {
    id: 'projects',
    label: 'Projects',
    summary:
      'Every survey project as Teslyn Field syncs it: progress per block, the lines-remaining map, reports, the refly list, diurnal QC, photos, flights, fuel dockets and crew.',
    clientSummary:
      'Your survey projects: progress per block, the map with the lines remaining and the project’s own aircraft, published reports, the refly list, diurnal QC and photos.',
  },
  reports: {
    id: 'reports',
    label: 'Reports',
    summary: 'Every project’s daily progress and weekly reports as PDFs, and publishing them to the client.',
    clientSummary: 'The daily progress and weekly reports published to you, as PDFs.',
  },
  insights: {
    id: 'insights',
    label: 'Insights',
    summary:
      'Production and productivity across your jobs, by project, aircraft and pilot, by week, month and financial year: km, km per hour, standby, reflys, fuel and QC. Old project files can be imported as history.',
  },
  deliverables: {
    id: 'deliverables',
    label: 'Deliverables',
    summary:
      'Each project’s share links to the data on your own storage (shown to the client when you choose), and the processors’ zips of each flight.',
    clientSummary: 'Links to the survey data shared with you.',
  },
  // R191 (decision 1010 item 2): the client portal's page; staff send and follow approvals where each item lives
  approvals: {
    id: 'approvals',
    label: 'Approvals',
    summary:
      'What the operator asks you to approve: survey block changes, the JSA, the JSP, the risk assessment and contracts to sign.',
  },
  // client portal only (R190, decision 1010): what each person of the client is told about, by email and push
  notices: {
    id: 'notices',
    label: 'Notices',
    summary: 'Choose what you are told about your projects, by email and by push on this device.',
  },
  ops: {
    id: 'ops',
    label: 'Operations Hub',
    summary:
      'Every project on one map with its progress, estimated finish and the tracked aircraft; and the figures: km per day, aircraft hours, weather and standby days, fuel, and progress against the plan and the contract.',
  },
  tracking: {
    id: 'tracking',
    label: 'Live tracking',
    summary:
      'A live map like Teslyn Field’s, one project at a time: your aircraft and vehicles and the public air traffic with their trails, the project’s lines, hazards and landholders; follow an aircraft and replay the last 48 hours.',
  },
  alerts: {
    id: 'alerts',
    label: 'Alerts',
    summary:
      'The alert history to acknowledge, snooze and resolve (SOS, lost tracking, base stations, space weather, crystal packs), and the notices you receive by push and email.',
  },
  stations: {
    id: 'stations',
    label: 'Station health',
    summary:
      'Your TA Live base stations: online or offline, the newest reading, the clock drift, time zone and latency of each sensor day, and the TA Live connection.',
  },
  spaceweather: {
    id: 'spaceweather',
    label: 'Space weather',
    summary:
      'What space weather means for flying today against a project’s own diurnal limits: the indices now, alerts and warnings, the next three days, the solar wind and CMEs.',
  },
  'hazard-packs': {
    id: 'hazard-packs',
    label: 'Hazard packs',
    summary:
      'Each project’s hazard pack as it syncs from Teslyn Field: what it holds, publishing it for Oz Runways, its link, and withdrawing a link.',
  },
  'safety-signoff': {
    id: 'safety-signoff',
    label: 'Safety sign-off',
    summary:
      'Each JSP safety meeting and JSA the crews sent for signing through your DocuSeal account: who has signed, the signed PDF and its audit log.',
  },
  'safety-register': {
    id: 'safety-register',
    label: 'Safety register',
    summary:
      'The accidents, incidents, near misses and landholder complaints the crews recorded in Teslyn Field, with their notification forms to print.',
    // R220: the client portal's page, the entries approved for the client
    clientSummary:
      'The accidents, incidents, near misses, landholder complaints and height breaches of your surveys that have been approved for you.',
  },
  clients: {
    id: 'clients',
    label: 'Clients',
    summary: 'Client organisations and their contacts, used on quotes and contracts.',
  },
  quotes: {
    id: 'quotes',
    label: 'Quotes',
    summary:
      'Survey proposals with areas, specifications, QC limits and prices; revisions and the quote PDF.',
  },
  contracts: {
    id: 'contracts',
    label: 'Contracts',
    summary:
      'Full and operator (OP) contracts, signing, QC variations and the project made from the contract.',
  },
  preview: {
    id: 'preview',
    label: 'Preview as client',
    summary: 'See the client portal exactly as a client organisation sees it, read only.',
  },
  people: {
    id: 'people',
    label: 'People',
    summary:
      'The staff register: everyone in the organisation with their roles and title; its pilots and operators fill every crew list. Tenant admins also invite people to sign in and set their roles here.',
  },
  licences: {
    id: 'licences',
    label: 'Licences',
    summary:
      'Your organisation’s Teslyn licence: seats in use, each person’s laptops with their Teslyn Field version and phones, releasing a seat or a device, and Braynewave’s invoices.',
  },
  users: {
    id: 'users',
    label: 'Users & roles',
    summary: 'Invite people, change their roles and remove access.',
  },
  aircraft: {
    id: 'aircraft',
    label: 'Aircraft',
    summary:
      "The aircraft register: each aircraft's type, engines, engine starts, oil uplift, fuel, tracker, MR hours and endurance.",
  },
  company: {
    id: 'company',
    label: 'Company',
    summary:
      'Company and payment details, clause templates and QC limit defaults printed in quotes and contracts; the final report’s standard text and equipment list; for tenant admins, the logo size, remote support and public air traffic.',
  },
  integrations: {
    id: 'integrations',
    label: 'Integrations',
    summary:
      'Your organisation’s own access keys and logins for trackers, aviation, weather, archive and sign-off services, stored encrypted.',
  },
  exports: {
    id: 'exports',
    label: 'Data exports',
    summary: 'A copy of all your organisation’s data, made every night, to download.',
  },
  diagnostics: {
    id: 'diagnostics',
    label: 'Diagnostics',
    summary:
      'Diagnostics your people sent from Teslyn Field: their notes, the app log, a picture of the window and the files they chose, kept 30 days.',
  },
  'mobile-app': {
    id: 'mobile-app',
    label: 'Teslyn Mobile',
    summary:
      'Get the phone app: scan the code with your Android phone, install it and sign in. It tells you when an update is ready.',
  },
  security: {
    id: 'security',
    label: 'Security',
    summary: 'Your own two-step sign-in with an authenticator app on your phone.',
  },
  tenants: {
    id: 'tenants',
    label: 'Tenants',
    summary: 'Every organisation on Teslyn: licence, seats, devices, service switches, exports and invoices.',
  },
  devices: {
    id: 'devices',
    label: 'Devices',
    summary:
      'Every organisation’s laptops with their Teslyn Field version, and the minimum version for sync.',
  },
  invoicing: {
    id: 'invoicing',
    label: 'Invoicing',
    summary:
      'Invoices Braynewave has sent each organisation, the costs it received (read from their PDFs), and the profit and loss.',
  },
  audit: {
    id: 'audit',
    label: 'Audit log',
    summary: 'Every recorded change across organisations, newest first.',
    // R166, decision 927: a company's own log for its tenant admins and managers
    workspaceSummary:
      'Who changed what and when, newest first: every recorded change, Teslyn Support’s access and each Preview as client. Search by kind of change, person, words and dates.',
  },
};

export type Area = 'workspace' | 'client' | 'platform';

/** The Home card's words for this area. */
export function pageSummary(id: PageId, area: Area): string {
  const def = PAGES[id];
  if (area === 'client') return def.clientSummary ?? def.summary;
  return area === 'workspace' ? (def.workspaceSummary ?? def.summary) : def.summary;
}

export type NavRole = TenantRole | ClientRole | 'platform_admin';

/**
 * The pages each role may open, in menu order. "Security" is in every list (each person's own sign-in) but is drawn
 * in the top bar, not the menu, except in Braynewave's console (C7, C11).
 */
const NAV_PAGES: Record<NavRole, readonly PageId[]> = {
  tenant_admin: [
    'home',
    'projects',
    'reports',
    'deliverables',
    'ops',
    'tracking',
    'alerts',
    'stations',
    'spaceweather',
    'hazard-packs',
    'safety-signoff',
    'safety-register',
    'clients',
    'quotes',
    'contracts',
    'preview',
    'people',
    'licences',
    'aircraft',
    'company',
    'integrations',
    'exports',
    'diagnostics',
    // R166, decision 927: the company's own audit log (as the audit_select policy)
    'audit',
    'mobile-app',
    'security',
  ],
  manager: [
    'home',
    'projects',
    'reports',
    'ops',
    'tracking',
    'alerts',
    'stations',
    'spaceweather',
    'hazard-packs',
    'safety-signoff',
    'safety-register',
    'clients',
    'quotes',
    'contracts',
    'preview',
    'people',
    'aircraft',
    'company',
    'integrations',
    'audit',
    'mobile-app',
    'security',
  ],
  field_crew: [
    'home',
    'projects',
    'reports',
    'tracking',
    'alerts',
    'stations',
    'spaceweather',
    'hazard-packs',
    'safety-signoff',
    'safety-register',
    'contracts',
    'people',
    'aircraft',
    'integrations',
    'mobile-app',
    'security',
  ],
  // Operations Hub read only (decision 231): the page has no actions
  office_staff: [
    'home',
    'projects',
    'reports',
    'ops',
    'alerts',
    'stations',
    'spaceweather',
    'hazard-packs',
    'safety-signoff',
    'safety-register',
    'clients',
    'quotes',
    'contracts',
    'people',
    'aircraft',
    'company',
    'integrations',
    'mobile-app',
    'security',
  ],
  data_processor: [
    'home',
    'projects',
    'reports',
    'deliverables',
    'alerts',
    'stations',
    'spaceweather',
    'hazard-packs',
    'safety-signoff',
    'safety-register',
    'contracts',
    'people',
    'aircraft',
    // decision 885: the final report's standard text and equipment list (Company's "Final report" tab only)
    'company',
    'integrations',
    'mobile-app',
    'security',
  ],
  // R156, decision 917: Teslyn Mobile only, no Teslyn Cloud page
  pilot: [],
  // decision 820: no "Live tracking" for clients; each project's "Map" shows its own aircraft and vehicles
  // R191: a client admin approves and signs; a client viewer reads what was asked and decided. R190 (decision 1010):
  // Notices, what each person is told about their projects by email and push. R220: the Safety register, the entries
  // approved for the client (viewer and admin alike)
  client_admin: [
    'home',
    'projects',
    'reports',
    'deliverables',
    'safety-register',
    'approvals',
    'notices',
    'users',
    'security',
  ],
  client_viewer: [
    'home',
    'projects',
    'reports',
    'deliverables',
    'safety-register',
    'approvals',
    'notices',
    'security',
  ],
  // Braynewave's admin console (docs/specs/cloud/admin-console.md); every page needs two-step sign-in. Unchanged by
  // the menu consolidation (C11, decision 844): Security stays in its menu. No Operations Hub since decision 800: a
  // company's figures only through Teslyn Support access (its own context); Users and roles stays for inviting a new
  // organisation's first tenant admin.
  platform_admin: ['home', 'tenants', 'devices', 'invoicing', 'audit', 'users', 'security'],
};

/** NAV_PAGES with Insights for the roles `canInsights` names (decision 1050): that rule alone decides who has it. */
export const NAV = Object.fromEntries(
  (Object.entries(NAV_PAGES) as [NavRole, readonly PageId[]][]).map(([role, pages]) => [
    role,
    canInsights([role]) ? pages.flatMap((p): PageId[] => (p === 'reports' ? [p, 'insights'] : [p])) : pages,
  ]),
) as Record<NavRole, readonly PageId[]>;

/** Display order of pages for a member with several roles. */
const PAGE_ORDER = Object.keys(PAGES) as PageId[];

/** Pages a member may open: the union of their roles' pages (one role keeps that role's own order). */
export function navFor(roles: readonly NavRole[]): readonly PageId[] {
  if (roles.length === 1) return NAV[roles[0]!];
  const allowed = new Set(roles.flatMap((r) => NAV[r]));
  return PAGE_ORDER.filter((p) => allowed.has(p));
}

/** Pages opened from the top bar, not the menu (C7): your own sign-in security, beside Sign out. */
export const TOP_BAR_PAGES: readonly PageId[] = ['security'];

export interface MenuGroup {
  /** The small heading over the group; null for Home at the top and for Braynewave's console. */
  readonly label: string | null;
  readonly pages: readonly PageId[];
}

/**
 * The menu's headings in order (C9, decision 855). Deliverables (kept by decision 843) sits under Projects, Safety
 * sign-off (R120, built after the review) beside Hazard packs, and a client admin's Users page under Company.
 */
export const MENU_GROUPS: readonly MenuGroup[] = [
  { label: 'Projects', pages: ['projects', 'reports', 'insights', 'deliverables', 'approvals', 'notices'] },
  {
    label: 'Live operations',
    pages: [
      'ops',
      'tracking',
      'alerts',
      'stations',
      'spaceweather',
      'hazard-packs',
      'safety-signoff',
      'safety-register',
    ],
  },
  { label: 'Commercial', pages: ['clients', 'quotes', 'contracts', 'preview'] },
  {
    label: 'Company',
    pages: [
      'people',
      'licences',
      'users',
      'aircraft',
      'company',
      'integrations',
      'exports',
      'diagnostics',
      // a company's own audit log (R166, decision 927); Braynewave's console keeps its own list
      'audit',
      // the phone app's download (docs/specs/mobile/mobile-distribution.md §3)
      'mobile-app',
    ],
  },
];

/**
 * The client portal's headings (R220): as the staff's, with the Safety register under Projects after Deliverables (a
 * client has no Live operations heading for it to sit under).
 */
const CLIENT_MENU_GROUPS: readonly MenuGroup[] = MENU_GROUPS.map((g) =>
  g.label === 'Projects'
    ? {
        ...g,
        pages: g.pages.flatMap((p): PageId[] => (p === 'deliverables' ? [p, 'safety-register'] : [p])),
      }
    : { ...g, pages: g.pages.filter((p) => p !== 'safety-register') },
);

/**
 * The menu for these pages: Home at the top, then each heading that has a page for this person, its pages in order
 * (a heading with none is left out). Braynewave's console keeps its one list with Security in it (C11).
 */
export function menuFor(nav: readonly PageId[], area: Area): readonly MenuGroup[] {
  if (area === 'platform') return [{ label: null, pages: nav }];
  const groups: MenuGroup[] = [];
  if (nav.includes('home')) groups.push({ label: null, pages: ['home'] });
  const placed = new Set<PageId>(['home', ...TOP_BAR_PAGES]);
  for (const g of area === 'client' ? CLIENT_MENU_GROUPS : MENU_GROUPS) {
    const pages = g.pages.filter((p) => nav.includes(p));
    pages.forEach((p) => placed.add(p));
    if (pages.length > 0) groups.push({ label: g.label, pages });
  }
  const rest = nav.filter((p) => !placed.has(p));
  if (rest.length > 0) groups.push({ label: null, pages: rest });
  return groups;
}

/** A place to open: a page, and the tab or view inside it (People's "signin", Projects' "deleted" or "flights"). */
export interface PageTarget {
  readonly page: PageId;
  readonly tab?: string;
}

/** Pages the menu consolidation moved, by their old id: a link or bookmark to one opens its new place. */
export const MOVED_PAGES: Readonly<Record<string, PageTarget>> = {
  progress: { page: 'projects', tab: 'progress' },
  bin: { page: 'projects', tab: 'deleted' },
  flights: { page: 'projects', tab: 'flights' },
  staff: { page: 'people', tab: 'staff' },
  documents: { page: 'company', tab: 'details' },
  settings: { page: 'company', tab: 'settings' },
};

const own = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

/** Where a page id opens in this area: today's id as it is, an old one at its new place; null when unknown. */
export function pageTarget(id: string, area: Area, tab?: string | null): PageTarget | null {
  // the staff workspace's Users & roles is People's second tab; client admins and the console keep their Users page
  if (id === 'users' && area === 'workspace') return { page: 'people', tab: 'signin' };
  if (own(MOVED_PAGES, id)) return MOVED_PAGES[id]!;
  if (!own(PAGES, id)) return null;
  const page = id as PageId;
  return tab && /^[a-z-]{1,20}$/.test(tab) ? { page, tab } : { page };
}

/** `?page=<id>` (and `&tab=<tab>`) of a link: the place it opens, or null without one. */
export function pageFromSearch(search: string, area: Area): PageTarget | null {
  const q = new URLSearchParams(search);
  const id = q.get('page');
  return id ? pageTarget(id, area, q.get('tab')) : null;
}

/** "Field crew, Data processor": the roles in words, for the top bar and the chooser. */
export function rolesLabel(roles: readonly NavRole[]): string {
  return roles.map((r) => ROLE_LABEL[r]).join(', ');
}
